import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { timeAgo, formatTimestamp } from '../../hooks/useTimeAgo'

interface RawMessage {
  id: string | number; subject?: string; content?: string; body?: string;
  sender_name?: string; from_id?: string; from_type?: string; sender_role?: string;
  community_name?: string; community_slug?: string; community_id?: string;
  created_at: string; read?: boolean; is_read?: number; starred?: boolean; is_starred?: number;
  folder?: string; attachments?: string | { filename: string; url: string }[]
}

interface CommunityMessage {
  id: string; subject?: string; content: string; sender_name: string; sender_role?: string;
  community_name?: string; created_at: string; read: boolean; starred: boolean; folder?: string;
  attachments: { filename: string; url: string }[]
}

function normalizeMessage(m: RawMessage): CommunityMessage {
  return {
    id: String(m.id),
    subject: m.subject || undefined,
    content: m.content || m.body || '',
    sender_name: m.sender_name || m.from_id || 'system',
    sender_role: m.sender_role,
    community_name: m.community_name,
    created_at: m.created_at,
    read: m.read ?? (m.is_read === 1),
    starred: m.starred ?? (m.is_starred === 1),
    folder: m.folder,
    attachments: typeof m.attachments === 'string' ? (JSON.parse(m.attachments || '[]')) : (m.attachments || []),
  }
}

function authHeaders(): Record<string, string> {
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Drag-to-resize hook
function useResize(initialWidth: number, minW: number, maxW: number) {
  const [width, setWidth] = useState(initialWidth)
  const dragging = useRef(false)
  const startX = useRef(0)
  const startW = useRef(0)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    startX.current = e.clientX
    startW.current = width
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return
      const delta = ev.clientX - startX.current
      setWidth(Math.max(minW, Math.min(maxW, startW.current + delta)))
    }
    const onUp = () => { dragging.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [width, minW, maxW])

  return { width, onMouseDown }
}

export default function InboxPage() {
  const sseOk = useSSEConnected()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [folder, setFolder] = useState('inbox')
  const [density, setDensity] = useState<'normal' | 'compact'>('normal')
  const [showCompose, setShowCompose] = useState(false)
  const [composeTo, setComposeTo] = useState('')
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [replyDraft, setReplyDraft] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [customFolders, setCustomFolders] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('inbox_custom_folders') || '[]') } catch { return [] }
  })
  const queryClient = useQueryClient()

  const folderResize = useResize(192, 140, 280)
  const listResize = useResize(320, 240, 500)

  const { data } = useQuery({
    queryKey: ['inbox', folder],
    queryFn: async () => {
      const res = await fetch(`/api/messages?folder=${folder}`, { headers: authHeaders() })
      if (!res.ok) return { messages: [] as CommunityMessage[], unread_count: 0 }
      const raw = await res.json()
      const rawMsgs: RawMessage[] = raw.messages || raw || []
      return { messages: rawMsgs.map(normalizeMessage), unread_count: raw.unread_count || 0 }
    },
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const messages: CommunityMessage[] = data?.messages || []
  const unreadCount = data?.unread_count || messages.filter(m => !m.read).length
  const selected = messages.find(m => m.id === selectedId)

  // Mutations
  const replyMut = useMutation({
    mutationFn: async () => {
      await fetch(`/api/messages/${selectedId}/reply`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ body: replyDraft }) })
    },
    onSuccess: () => { setReplyDraft(''); queryClient.invalidateQueries({ queryKey: ['inbox'] }) },
  })

  const composeMut = useMutation({
    mutationFn: async () => {
      await fetch('/api/messages', { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ to: composeTo, subject: composeSubject, body: composeBody }) })
    },
    onSuccess: () => { setShowCompose(false); setComposeTo(''); setComposeSubject(''); setComposeBody(''); queryClient.invalidateQueries({ queryKey: ['inbox'] }) },
  })

  const bulkMut = useMutation({
    mutationFn: async ({ action, ids }: { action: string; ids: string[] }) => {
      await Promise.all(ids.map(id => fetch(`/api/messages/${id}/${action}`, { method: 'POST', headers: authHeaders() })))
    },
    onSuccess: () => { setSelectedIds(new Set()); setSelectedId(null); queryClient.invalidateQueries({ queryKey: ['inbox'] }) },
  })

  // Select all toggle
  const allSelected = messages.length > 0 && messages.every(m => selectedIds.has(m.id))
  function toggleSelectAll() {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(messages.map(m => m.id)))
  }
  function toggleSelect(id: string) {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  // Custom folders
  function addCustomFolder() {
    if (!newFolderName.trim()) return
    const updated = [...customFolders, newFolderName.trim()]
    setCustomFolders(updated)
    localStorage.setItem('inbox_custom_folders', JSON.stringify(updated))
    setNewFolderName('')
    setShowNewFolder(false)
  }

  const folders = [
    { key: 'inbox', label: 'Inbox', icon: '📥', count: unreadCount },
    { key: 'sent', label: 'Sent', icon: '📤' },
    { key: 'starred', label: 'Starred', icon: '⭐' },
    { key: 'archive', label: 'Archive', icon: '📦' },
    { key: 'trash', label: 'Trash', icon: '🗑' },
  ]

  const isCompact = density === 'compact'
  const hasSelection = selectedIds.size > 0

  return (
    <div className="flex h-full">
      {/* Folder sidebar */}
      <div className="shrink-0 py-4 px-2 flex flex-col" style={{ width: folderResize.width, background: 'var(--bg-surface)', borderRight: 'none' }}>
        <div className="flex items-center justify-between px-2 mb-4">
          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Messages</span>
          <button onClick={() => setShowCompose(true)} className="text-xs px-2.5 py-1 rounded font-semibold" style={{ background: 'var(--accent)', color: '#fff' }}>
            Compose
          </button>
        </div>
        {folders.map(f => (
          <button
            key={f.key}
            onClick={() => { setFolder(f.key); setSelectedId(null); setSelectedIds(new Set()) }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors mb-0.5"
            style={{ background: folder === f.key ? 'var(--bg-elevated)' : 'transparent', color: folder === f.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}
          >
            <span>{f.icon}</span>
            <span className="flex-1">{f.label}</span>
            {f.count != null && f.count > 0 && (
              <span className="text-xs font-bold px-1.5 rounded-full" style={{ background: 'var(--warning)', color: '#000', fontSize: 10 }}>{f.count}</span>
            )}
          </button>
        ))}

        {/* Custom folders */}
        {customFolders.length > 0 && (
          <>
            <div className="mt-3 mb-1 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Folders</div>
            {customFolders.map(cf => (
              <button
                key={cf}
                onClick={() => { setFolder(cf); setSelectedId(null); setSelectedIds(new Set()) }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors mb-0.5"
                style={{ background: folder === cf ? 'var(--bg-elevated)' : 'transparent', color: folder === cf ? 'var(--text-primary)' : 'var(--text-secondary)' }}
              >
                <span>📂</span><span className="flex-1">{cf}</span>
              </button>
            ))}
          </>
        )}

        <div className="mt-2 px-2">
          {showNewFolder ? (
            <div className="flex gap-1">
              <input
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustomFolder(); if (e.key === 'Escape') setShowNewFolder(false) }}
                placeholder="Folder name"
                className="flex-1 px-2 py-1 rounded text-xs outline-none"
                style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                autoFocus
              />
              <button onClick={addCustomFolder} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--accent)', color: '#fff' }}>+</button>
            </div>
          ) : (
            <button onClick={() => setShowNewFolder(true)} className="text-xs w-full text-left px-3 py-1.5 rounded-md" style={{ color: 'var(--text-muted)' }}>
              + New folder
            </button>
          )}
        </div>
      </div>

      {/* Resize gutter 1 */}
      <div
        onMouseDown={folderResize.onMouseDown}
        className="w-1.5 shrink-0 cursor-col-resize hover:bg-[var(--accent)] transition-colors"
        style={{ background: 'var(--border)' }}
      />

      {/* Message list */}
      <div className="shrink-0 overflow-y-auto flex flex-col" style={{ width: listResize.width, borderRight: 'none' }}>
        {/* Toolbar */}
        <div className="shrink-0 flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="w-3.5 h-3.5 shrink-0"
            style={{ accentColor: 'var(--accent)' }}
            title="Select all"
          />
          {hasSelection ? (
            <>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{selectedIds.size} selected</span>
              <button onClick={() => bulkMut.mutate({ action: 'archive', ids: [...selectedIds] })} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Archive</button>
              <button onClick={() => bulkMut.mutate({ action: 'delete', ids: [...selectedIds] })} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--danger)', border: '1px solid var(--border)' }}>Delete</button>
            </>
          ) : (
            <span className="text-xs flex-1" style={{ color: 'var(--text-muted)' }}>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
          )}
          <div className="flex-1" />
          <button onClick={() => setDensity(d => d === 'normal' ? 'compact' : 'normal')} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)' }} title="Toggle density">
            {isCompact ? '▤' : '▥'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="p-4 text-xs text-center" style={{ color: 'var(--text-muted)' }}>No messages</div>
          ) : messages.map(m => (
            <div
              key={m.id}
              className="flex items-start gap-2.5 transition-colors cursor-pointer"
              onClick={() => setSelectedId(m.id)}
              style={{
                padding: isCompact ? '6px 12px' : '12px',
                background: selectedId === m.id ? 'var(--bg-elevated)' : 'transparent',
                borderBottom: '1px solid var(--border)',
                borderLeft: !m.read ? '3px solid var(--warning)' : '3px solid transparent',
              }}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(m.id)}
                onChange={e => { e.stopPropagation(); toggleSelect(m.id) }}
                onClick={e => e.stopPropagation()}
                className="w-3.5 h-3.5 mt-1 shrink-0"
                style={{ accentColor: 'var(--accent)' }}
              />
              <PersonaAvatar name={m.sender_name} size={isCompact ? 24 : 32} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-xs font-medium truncate" style={{ color: !m.read ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{m.sender_name}</span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)', fontSize: isCompact ? 10 : 11 }}>{timeAgo(m.created_at)}</span>
                </div>
                {m.subject && <div className="text-xs truncate" style={{ color: !m.read ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: isCompact ? 11 : 12 }}>{m.subject}</div>}
                {!isCompact && <div className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>{m.content.substring(0, 80)}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resize gutter 2 */}
      <div
        onMouseDown={listResize.onMouseDown}
        className="w-1.5 shrink-0 cursor-col-resize hover:bg-[var(--accent)] transition-colors"
        style={{ background: 'var(--border)' }}
      />

      {/* Reading pane */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selected ? (
          <>
            <div className="shrink-0 px-7 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
              {selected.subject && <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{selected.subject}</h2>}
              <div className="flex items-center gap-3">
                <PersonaAvatar name={selected.sender_name} size={36} />
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{selected.sender_name}</div>
                  {selected.sender_role && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{selected.sender_role}</div>}
                </div>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatTimestamp(selected.created_at)}</span>
                {selected.community_name && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{selected.community_name}</span>}
              </div>
              <div className="flex gap-1.5 mt-3">
                <button onClick={() => document.getElementById('reply-input')?.focus()} className="text-xs px-3 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Reply</button>
                <button onClick={() => { setShowCompose(true); setComposeSubject(`Fwd: ${selected.subject || ''}`); setComposeBody(`\n\n--- Forwarded ---\nFrom: ${selected.sender_name}\n\n${selected.content}`) }} className="text-xs px-3 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Forward</button>
                <button onClick={() => bulkMut.mutate({ action: 'archive', ids: [selected.id] })} className="text-xs px-3 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Archive</button>
                <button onClick={() => bulkMut.mutate({ action: 'delete', ids: [selected.id] })} className="text-xs px-3 py-1.5 rounded" style={{ background: 'var(--bg-elevated)', color: 'var(--danger)', border: '1px solid var(--border)' }}>Delete</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6">
              <div className="max-w-2xl text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <MarkdownRenderer content={selected.content} />
              </div>
              {selected.attachments.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-4">
                  {selected.attachments.map((a, i) => (
                    <a key={i} href={a.url} download className="text-xs px-3 py-1.5 rounded no-underline" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>📎 {a.filename}</a>
                  ))}
                </div>
              )}
            </div>

            <div className="shrink-0 px-7 py-3" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
              <textarea id="reply-input" value={replyDraft} onChange={e => setReplyDraft(e.target.value)} placeholder="Reply..." rows={2} className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-y" style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)', minHeight: 48 }} />
              {replyDraft.trim() && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => replyMut.mutate()} disabled={replyMut.isPending} className="text-xs px-4 py-1.5 rounded font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#fff' }}>Send Reply</button>
                  <button onClick={() => setReplyDraft('')} className="text-xs px-3 py-1.5" style={{ color: 'var(--text-muted)' }}>Cancel</button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Select a message to read</p>
          </div>
        )}
      </div>

      {/* Compose modal */}
      {showCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(10,22,40,0.8)' }}>
          <div className="w-[500px] rounded-xl flex flex-col" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', maxHeight: '80vh', boxShadow: '0 16px 64px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
              <h2 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>New Message</h2>
              <button onClick={() => { setShowCompose(false); setComposeBody(''); setComposeSubject('') }} className="text-lg" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
            </div>
            <div className="p-5 flex flex-col gap-3 flex-1 overflow-y-auto">
              <input type="text" placeholder="To" value={composeTo} onChange={e => setComposeTo(e.target.value)} className="w-full px-3 py-2 rounded text-sm outline-none" style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
              <input type="text" placeholder="Subject" value={composeSubject} onChange={e => setComposeSubject(e.target.value)} className="w-full px-3 py-2 rounded text-sm outline-none" style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
              <textarea placeholder="Message..." value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={6} className="w-full px-3 py-2 rounded text-sm outline-none resize-y" style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
            </div>
            <div className="flex gap-2 px-5 py-3" style={{ borderTop: '1px solid var(--border)' }}>
              <button onClick={() => composeMut.mutate()} disabled={!composeTo.trim() || !composeBody.trim() || composeMut.isPending} className="text-xs px-4 py-2 rounded font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#fff' }}>Send</button>
              <button onClick={() => { setShowCompose(false); setComposeBody(''); setComposeSubject('') }} className="text-xs px-3 py-2" style={{ color: 'var(--text-muted)' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
